import { GaChartPoint, GaGeographyRow, GaOverview, GaReferralRow, GaReportResponse, GaRow } from './google-analytics-report.models';

function numberValue(row: GaRow, index: number): number {
  const value = Number(row.metricValues?.[index]?.value ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function dimensionValue(row: GaRow, index: number, fallback: string = '(not set)'): string {
  return row.dimensionValues?.[index]?.value || fallback;
}

export function mapOverview(report: GaReportResponse | undefined): GaOverview {
  const row = report?.rows?.[0];
  return {
    visits: row ? numberValue(row, 0) : 0,
    activeUsers: row ? numberValue(row, 1) : 0,
    newUsers: row ? numberValue(row, 2) : 0,
    avgSessionDuration: row ? numberValue(row, 3) : 0,
    pagesPerVisit: row ? numberValue(row, 4) : 0,
    bounceRate: row ? numberValue(row, 5) : 0,
    pageViews: row ? numberValue(row, 6) : 0,
    keyEvents: row ? numberValue(row, 7) : 0
  };
}

export function mapReferrals(report: GaReportResponse | undefined): GaReferralRow[] {
  const aggregates = new Map<string, { sessions: number; newUsers: number; views: number; engagedSessions: number }>();
  for (const row of report?.rows ?? []) {
    const source = dimensionValue(row, 0).split(' / ')[0].trim();
    if (!source || source === '(not set)') {
      continue;
    }
    const current = aggregates.get(source) ?? { sessions: 0, newUsers: 0, views: 0, engagedSessions: 0 };
    current.sessions += numberValue(row, 0);
    current.newUsers += numberValue(row, 1);
    current.views += numberValue(row, 2);
    current.engagedSessions += numberValue(row, 3);
    aggregates.set(source, current);
  }
  const referralSessions = Array.from(aggregates.values()).reduce((sum, item) => sum + item.sessions, 0);
  return Array.from(aggregates.entries()).map(([referralSource, item]) => ({
    referralSource,
    visits: item.sessions,
    visitsPercentage: referralSessions ? ((item.sessions / referralSessions) * 100).toFixed(2) : '0.00',
    newVisits: item.newUsers,
    pageviewsPerVisit: item.sessions ? (item.views / item.sessions).toFixed(2) : '0.00',
    bounceRate: item.sessions ? ((1 - item.engagedSessions / item.sessions) * 100).toFixed(2) : '0.00'
  })).sort((a, b) => b.visits - a.visits);
}

export function mapGeography(report: GaReportResponse | undefined): GaGeographyRow[] {
  const aggregates = new Map<string, GaGeographyRow>();
  for (const row of report?.rows ?? []) {
    const country = dimensionValue(row, 0);
    const city = dimensionValue(row, 1);
    const key = `${country}\u0000${city}`;
    const current = aggregates.get(key) ?? { country, city, sessions: 0, activeUsers: 0, newUsers: 0 };
    current.sessions += numberValue(row, 0);
    current.activeUsers += numberValue(row, 1);
    current.newUsers += numberValue(row, 2);
    aggregates.set(key, current);
  }
  return Array.from(aggregates.values()).sort((a, b) => b.sessions - a.sessions);
}

export function mapLabelValue(report: GaReportResponse | undefined, includeSecondDimension: boolean = false): GaChartPoint[] {
  return (report?.rows ?? []).map((row) => {
    const first = dimensionValue(row, 0);
    const second = includeSecondDimension ? dimensionValue(row, 1, '') : '';
    return { label: second ? `${first} (${second})` : first, value: numberValue(row, 0) };
  });
}

export function mapDailyTrend(report: GaReportResponse | undefined): { sessions: GaChartPoint[]; bounceRate: GaChartPoint[] } {
  const rows = report?.rows ?? [];
  return {
    sessions: rows.map((row) => ({ week: dimensionValue(row, 0), sessions: numberValue(row, 0) })),
    bounceRate: rows.map((row) => ({ week: dimensionValue(row, 0), sessions: numberValue(row, 1) }))
  };
}
